import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Avatar,
  useTheme,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Person,
  Work,
  AccountBalance,
  Business,
  Star,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Badge,
} from "@mui/icons-material";
import moment from "moment";

const DetailKaryawanModal = ({ dataDetail, open, onClose }) => {
  const theme = useTheme();

  const renderLabelKompetensi = (value) => {
    if (value < 3) {
      return { label: "Kurang Baik", color: "error" };
    }
    if (value < 5) {
      return { label: "Baik", color: "warning" };
    } else {
      return { label: "Baik Sekali", color: "success" };
    }
  };

  const InfoItem = ({ icon, label, value }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mb: 2,
        mr: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 2,
          backgroundColor: `${theme.palette.primary.main}15`,
          mr: 2,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={0.5}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );

  const SectionHeader = ({ icon, title }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        boxShadow: "none",
        px: 2,
        py: 1.5,
        backgroundColor: `${theme.palette.background.paper}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: 1.5,
          backgroundColor: `${theme.palette.primary.main}15`,
          mr: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: `${theme.palette.primary.main}08`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: 3 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mr: 2,
              backgroundColor: theme.palette.primary.main,
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            {dataDetail?.nama_karyawan?.charAt(0) || "K"}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {dataDetail?.nama_karyawan || "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dataDetail?.id_karyawan || "-"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Informasi Pribadi */}
          <Grid item xs={12} md={6}>
            <SectionHeader
              icon={<Person fontSize="small" />}
              title="Informasi Pribadi"
            />
            <Box sx={{ pt: 2 }}>
              <InfoItem
                icon={
                  <Person
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="NIK"
                value={dataDetail?.nik}
              />
              <InfoItem
                icon={
                  <Email
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="Email"
                value={dataDetail?.email}
              />
              <InfoItem
                icon={
                  <Phone
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="Nomor HP"
                value={dataDetail?.no_hp}
              />
              <InfoItem
                icon={
                  <LocationOn
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="Alamat"
                value={dataDetail?.alamat}
              />
              <InfoItem
                icon={
                  <CalendarToday
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="Tanggal Lahir"
                value={
                  dataDetail?.tanggal_lahir
                    ? moment(dataDetail.tanggal_lahir).format("DD MMMM YYYY")
                    : "-"
                }
              />
              <InfoItem
                icon={
                  <Badge
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="Usia"
                value={
                  dataDetail?.tanggal_lahir
                    ? `${moment().diff(moment(dataDetail.tanggal_lahir), "years")} Tahun`
                    : "-"
                }
              />
              <InfoItem
                icon={
                  <Person
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                }
                label="Jenis Kelamin"
                value={
                  dataDetail?.jenis_kelamin === "laki-laki"
                    ? "Laki - Laki"
                    : dataDetail?.jenis_kelamin === "perempuan"
                      ? "Perempuan"
                      : dataDetail?.jenis_kelamin
                }
              />
            </Box>
          </Grid>

          {/* Informasi Pekerjaan */}
          <Grid item xs={12} md={6}>
            <SectionHeader
              icon={<Work fontSize="small" />}
              title="Informasi Pekerjaan"
            />
            <Box sx={{ pt: 2 }}>
              <InfoItem
                icon={
                  <Badge
                    fontSize="small"
                    sx={{ color: theme.palette.info.main }}
                  />
                }
                label="Kode Jabatan"
                value={dataDetail?.kode_jabatan}
              />
              <InfoItem
                icon={
                  <Work
                    fontSize="small"
                    sx={{ color: theme.palette.info.main }}
                  />
                }
                label="Nama Jabatan"
                value={dataDetail?.nama_jabatan}
              />
              <InfoItem
                icon={
                  <CalendarToday
                    fontSize="small"
                    sx={{ color: theme.palette.info.main }}
                  />
                }
                label="Tanggal Mulai Kerja"
                value={
                  dataDetail?.tanggal_mulai_kerja
                    ? moment(dataDetail.tanggal_mulai_kerja).format(
                        "DD MMMM YYYY",
                      )
                    : "-"
                }
              />
              <InfoItem
                icon={
                  <CalendarToday
                    fontSize="small"
                    sx={{ color: theme.palette.info.main }}
                  />
                }
                label="Lama Kerja"
                value={`${dataDetail?.lama_kerja_hari || 0} Hari`}
              />
              <InfoItem
                icon={
                  <Work
                    fontSize="small"
                    sx={{ color: theme.palette.info.main }}
                  />
                }
                label="Tipe Jenjang"
                value={dataDetail?.tipe_jenjang}
              />
              <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mr: 2 }}
                >
                  Status Aktif:
                </Typography>
                <Chip
                  label={
                    dataDetail?.active === "true" ? "Aktif" : "Tidak Aktif"
                  }
                  color={dataDetail?.active === "true" ? "success" : "error"}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          </Grid>

          {/* Informasi Bank */}
          <Grid item xs={12} md={6}>
            <SectionHeader
              icon={<AccountBalance fontSize="small" />}
              title="Informasi Bank"
            />
            <Box sx={{ pt: 2 }}>
              <InfoItem
                icon={
                  <AccountBalance
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                }
                label="Tipe Agunan"
                value={dataDetail?.tipe_agunan}
              />
              <InfoItem
                icon={
                  <AccountBalance
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                }
                label="Nama Bank"
                value={dataDetail?.nama_bank}
              />
              <InfoItem
                icon={
                  <AccountBalance
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                }
                label="Atas Nama Rekening"
                value={dataDetail?.atas_nama_rekening}
              />
              <InfoItem
                icon={
                  <AccountBalance
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                }
                label="Nomor Rekening"
                value={dataDetail?.nomor_rekening}
              />
            </Box>
          </Grid>

          {/* Informasi Cabang */}
          <Grid item xs={12} md={6}>
            <SectionHeader
              icon={<Business fontSize="small" />}
              title="Informasi Cabang"
            />
            <Box sx={{ pt: 2 }}>
              <InfoItem
                icon={
                  <Business
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                }
                label="ID Cabang"
                value={dataDetail?.id_cab}
              />
              <InfoItem
                icon={
                  <Business
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                }
                label="Nama Cabang"
                value={dataDetail?.nama_cab}
              />
              <InfoItem
                icon={
                  <LocationOn
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                }
                label="Kota Cabang"
                value={dataDetail?.kota_cab}
              />
              <InfoItem
                icon={
                  <LocationOn
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                }
                label="Alamat Cabang"
                value={dataDetail?.alamat_cab}
              />
              <InfoItem
                icon={
                  <Phone
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                }
                label="Telepon Cabang"
                value={dataDetail?.telp_cab}
              />
            </Box>
          </Grid>

          {/* Kompetensi */}
          <Grid item xs={12}>
            <SectionHeader
              icon={<Star fontSize="small" />}
              title="Kompetensi"
            />
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={4}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 2,
                    backgroundColor: `${theme.palette.secondary.main}08`,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={1}
                  >
                    Sikap
                  </Typography>
                  <Chip
                    label={renderLabelKompetensi(dataDetail?.sikap).label}
                    color={renderLabelKompetensi(dataDetail?.sikap).color}
                    size="small"
                  />
                  <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                    {dataDetail?.sikap || 0}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 2,
                    backgroundColor: `${theme.palette.secondary.main}08`,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={1}
                  >
                    Keterampilan
                  </Typography>
                  <Chip
                    label={
                      renderLabelKompetensi(dataDetail?.keterampilan).label
                    }
                    color={
                      renderLabelKompetensi(dataDetail?.keterampilan).color
                    }
                    size="small"
                  />
                  <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                    {dataDetail?.keterampilan || 0}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 2,
                    backgroundColor: `${theme.palette.secondary.main}08`,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={1}
                  >
                    Pengetahuan
                  </Typography>
                  <Chip
                    label={renderLabelKompetensi(dataDetail?.pengetahuan).label}
                    color={renderLabelKompetensi(dataDetail?.pengetahuan).color}
                    size="small"
                  />
                  <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                    {dataDetail?.pengetahuan || 0}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
        <Button
          type="submit"
          variant="contained"
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailKaryawanModal;
